import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import httpClient from "@/dataprovider/http-client";
import { getApiUrl } from "@/constants";
import {
  dailySummaryEmptyMessage,
  filterDailySummaryRows,
  rowAddress,
  rowSchedule,
  rowThaliLabel,
  sortDailySummaryRows,
  type DailySummaryRow,
  type DailySummarySortColumn,
} from "./daily-summary-rows";

type DailySummaryPayload = {
  rows?: DailySummaryRow[];
  hasDailyMenu?: boolean;
  count?: number;
};

const EMPTY_ROWS: DailySummaryRow[] = [];

type DailySummarySectionProps = {
  date: string;
  onHasDailyMenu?: (hasDailyMenu: boolean) => void;
};

export default function DailySummarySection({ date, onHasDailyMenu }: DailySummarySectionProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [payload, setPayload] = useState<DailySummaryPayload | null>(null);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<DailySummarySortColumn>("thali");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPayload(null);
    setSearch("");
    const q = `${getApiUrl()}/fmbThaliDelivery/daily-summary?date=${encodeURIComponent(date)}`;
    httpClient(q)
      .then(({ json }) => {
        if (cancelled) return;
        const next = json as DailySummaryPayload;
        setPayload(next);
        onHasDailyMenu?.(next.hasDailyMenu !== false);
      })
      .catch((e: { message?: string }) => {
        if (cancelled) return;
        setError(e?.message || "Request failed");
        onHasDailyMenu?.(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [date, onHasDailyMenu]);

  const rawRows = payload?.rows ?? EMPTY_ROWS;
  const meta = useMemo(() => {
    if (!payload) {
      return null;
    }
    return {
      hasDailyMenu: payload.hasDailyMenu !== false,
      count: payload.count,
    };
  }, [payload]);

  const filteredRows = useMemo(() => filterDailySummaryRows(rawRows, search), [rawRows, search]);

  const displayRows = useMemo(
    () => sortDailySummaryRows(filteredRows, sortBy, sortDir),
    [filteredRows, sortBy, sortDir]
  );

  const emptyMessage = dailySummaryEmptyMessage({
    loading,
    hasError: Boolean(error),
    hasPayload: payload != null,
    hasDailyMenu: meta?.hasDailyMenu !== false,
    rowCount: rawRows.length,
    visibleCount: displayRows.length,
  });
  const showToolbar = Boolean(meta?.hasDailyMenu && rawRows.length > 0);
  const showTable = displayRows.length > 0;

  const requestSort = (col: DailySummarySortColumn) => {
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDir("asc");
    }
  };

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Daily delivery summary
      </Typography>
      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      ) : null}
      {emptyMessage ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }}>
          {emptyMessage}
        </Typography>
      ) : null}
      {showToolbar ? (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography variant="body2">
            Deliveries expected: {meta?.count ?? rawRows.length}
            {search.trim() ? (
              <Box component="span" sx={{ ml: 1, color: "text.secondary" }}>
                (showing {displayRows.length} of {rawRows.length} loaded)
              </Box>
            ) : null}
          </Typography>
          <TextField
            label="Filter table"
            placeholder="Thali, file, name, address, schedule…"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 240, maxWidth: 400 }}
          />
        </Box>
      ) : null}
      {showTable ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortBy === "thali"}
                  direction={sortBy === "thali" ? sortDir : "asc"}
                  onClick={() => requestSort("thali")}
                >
                  Thali
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === "fileNo"}
                  direction={sortBy === "fileNo" ? sortDir : "asc"}
                  onClick={() => requestSort("fileNo")}
                >
                  FMB
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === "name"}
                  direction={sortBy === "name" ? sortDir : "asc"}
                  onClick={() => requestSort("name")}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === "address"}
                  direction={sortBy === "address" ? sortDir : "asc"}
                  onClick={() => requestSort("address")}
                >
                  Address
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === "schedule"}
                  direction={sortBy === "schedule" ? sortDir : "asc"}
                  onClick={() => requestSort("schedule")}
                >
                  Schedule
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayRows.map((row: DailySummaryRow) => (
              <TableRow key={row.fmbThaliId}>
                <TableCell>{rowThaliLabel(row) || "—"}</TableCell>
                <TableCell>{row.fileNo ?? "—"}</TableCell>
                <TableCell>{row.name ?? "—"}</TableCell>
                <TableCell>{rowAddress(row) || "—"}</TableCell>
                <TableCell>{rowSchedule(row) || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}
    </Paper>
  );
}
